import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addDays, subDays } from 'date-fns';
import HabitForm from './forms/HabitForm';
import HabitProgressGraph from './HabitProgressGraph';
import { Habit, HabitInstance, HabitTrackingStatus } from '../types/habit';

interface HabitDetailViewProps {
  habitId: number;
  onBack?: () => void;
}

const HabitDetailView: React.FC<HabitDetailViewProps> = ({ habitId, onBack }) => {
  const [habit, setHabit] = useState<Habit | null>(null);
  const [instances, setInstances] = useState<HabitInstance[]>([]);
  const [trackingStatus, setTrackingStatus] = useState<HabitTrackingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNotes, setShowNotes] = useState<number | null>(null);
  const [newNote, setNewNote] = useState('');
  const [updatingInstance, setUpdatingInstance] = useState<number | null>(null);
  const [weeklyProgress, setWeeklyProgress] = useState<Array<{ date: Date; status: 'completed' | 'not_completed' | 'pending' | 'not_tracked'; instance?: HabitInstance }>>([]);
  const [monthlyProgress, setMonthlyProgress] = useState<Array<{ date: Date; status: 'completed' | 'not_completed' | 'pending' | 'not_tracked'; instance?: HabitInstance }>>([]);

  // Function to get status for a specific date using the new API
  const getStatusForDate = useCallback(async (date: Date): Promise<'completed' | 'not_completed' | 'pending' | 'not_tracked'> => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await apiClient.get(`/api/habits/${habitId}/status_for_date/?date=${dateStr}`);
      return response.data.status;
    } catch (err) {
      console.error("Error fetching status for date:", date, err);
      return 'not_tracked';
    }
  }, [habitId]);

  // Function to update progress data based on new API endpoints
  const updateProgressData = useCallback(async (instancesData: HabitInstance[], trackingData: HabitTrackingStatus | null) => {
    // Update weekly progress
    const start = startOfWeek(selectedDate);
    const end = endOfWeek(selectedDate);
    const weekDays = eachDayOfInterval({ start, end });

    const weeklyData = await Promise.all(weekDays.map(async (day) => {
      const status = await getStatusForDate(day);
      const instance = instancesData.find(inst => isSameDay(parseISO(inst.date), day));
      return {
        date: day,
        status: status,
        instance: instance
      };
    }));
    setWeeklyProgress(weeklyData);

    // Update monthly progress
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const monthlyData = await Promise.all(monthDays.map(async (day) => {
      const status = await getStatusForDate(day);
      const instance = instancesData.find(inst => isSameDay(parseISO(inst.date), day));
      return {
        date: day,
        status: status,
        instance: instance
      };
    }));
    setMonthlyProgress(monthlyData);
  }, [selectedDate, getStatusForDate]);

  const fetchHabitData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate = format(startOfWeek(selectedDate), 'yyyy-MM-dd');
      const endDate = format(endOfWeek(selectedDate), 'yyyy-MM-dd');

      const [habitRes, instancesRes, trackingRes] = await Promise.all([
        apiClient.get(`/api/habits/${habitId}/`),
        apiClient.get(`/api/habit-instances/?habit_id=${habitId}`),
        apiClient.get(`/api/habits/${habitId}/tracking_status/?start_date=${startDate}&end_date=${endDate}`)
      ]);

      setHabit(habitRes.data);
      setInstances(instancesRes.data || []);
      setTrackingStatus(trackingRes.data);

      // Update progress data
      await updateProgressData(instancesRes.data || [], trackingRes.data);
    } catch (err: unknown) {
      console.error("Error fetching habit data:", err);
      setError("Failed to load habit data.");
    } finally {
      setLoading(false);
    }
  }, [habitId, selectedDate, updateProgressData]);

  // Function to get styles based on status
  const getStatusStyles = (status: 'completed' | 'not_completed' | 'pending' | 'not_tracked'): React.CSSProperties => {
    switch (status) {
      case 'completed':
        return {
          backgroundColor: '#DCFCE7',
          borderColor: '#86EFAC',
          color: '#166534',
        };
      case 'not_completed':
        return {
          backgroundColor: '#FEE2E2',
          borderColor: '#FCA5A5',
          color: '#991B1B',
        };
      case 'pending':
        return {
          backgroundColor: '#FEF3C7',
          borderColor: '#FCD34D',
          color: '#92400E',
        };
      default:
        return {
          backgroundColor: COLORS.bg.base,
          borderColor: COLORS.border.default,
          color: COLORS.text.secondary,
        };
    }
  };

  // Function to get icon based on status
  const getStatusIcon = (status: 'completed' | 'not_completed' | 'pending' | 'not_tracked') => {
    const iconStyle: React.CSSProperties = { width: '16px', height: '16px', margin: '4px auto 0' };
    switch (status) {
      case 'completed':
        return <CheckCircleIcon style={iconStyle} />;
      case 'not_completed':
        return <XCircleIcon style={iconStyle} />;
      case 'pending':
        return <ClockIcon style={iconStyle} />;
      default:
        return <XCircleIcon style={iconStyle} />;
    }
  };

  useEffect(() => {
    fetchHabitData();
  }, [fetchHabitData]);

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (!habit || !window.confirm('Are you sure you want to delete this habit?')) return;

    try {
      const response = await apiClient.delete(`/api/habits/${habit.id}/`);

      if (response.status >= 200 && response.status < 300) {
        if (onBack) onBack();
      } else {
        throw new Error(`Failed to delete habit (${response.status})`);
      }
    } catch (err: unknown) {
      console.error("Error deleting habit:", err);
      setError("Failed to delete habit. Please try again.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleHabitUpdated = () => {
    fetchHabitData();
    setIsEditModalOpen(false);
  };

  const toggleHabitCompletion = async (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingInstance = instances.find(instance =>
      isSameDay(parseISO(instance.date), date)
    );

    setUpdatingInstance(existingInstance?.id || -1);

    try {
      if (existingInstance) {
        // If instance exists, toggle its completion status
        const newCompleted = !existingInstance.completed;
        const endpoint = newCompleted
          ? `/api/habits/${habitId}/mark_completed/`
          : `/api/habits/${habitId}/mark_not_completed/`;

        const response = await apiClient.post(endpoint, {
          date: dateStr,
          notes: existingInstance.notes || null
        });

        if (response.status >= 200 && response.status < 300) {
          // Refresh data after successful update
          await fetchHabitData();
        } else {
          throw new Error(`Failed to update habit (${response.status})`);
        }
      } else {
        // Create new completed instance
        const response = await apiClient.post(`/api/habits/${habitId}/mark_completed/`, {
          date: dateStr,
          notes: null
        });

        if (response.status >= 200 && response.status < 300) {
          // Refresh data after successful creation
          await fetchHabitData();
        } else {
          throw new Error(`Failed to create habit instance (${response.status})`);
        }
      }
    } catch (err: unknown) {
      console.error("Error toggling habit completion:", err);
      setError("Failed to update habit. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setUpdatingInstance(null);
    }
  };

  const updateInstanceNote = async (instanceId: number, notes: string) => {
    try {
      const response = await apiClient.put(`/api/habit-instances/${instanceId}/`, {
        notes: notes
      });

      if (response.status >= 200 && response.status < 300) {
        // Update local state
        setInstances(prev =>
          prev.map(inst =>
            inst.id === instanceId
              ? { ...inst, notes: notes }
              : inst
          )
        );
        setShowNotes(null);
        setNewNote('');
      } else {
        throw new Error(`Failed to update notes (${response.status})`);
      }
    } catch (err: unknown) {
      console.error("Error updating instance note:", err);
      setError("Failed to update notes. Please try again.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const getFrequencyStyles = (frequency: string): React.CSSProperties => {
    switch (frequency) {
      case 'daily':
        return { backgroundColor: '#DBEAFE', color: '#1E40AF' };
      case 'weekly':
        return { backgroundColor: '#F3E8FF', color: '#6B21A8' };
      case 'monthly':
        return { backgroundColor: '#DCFCE7', color: '#166534' };
      default:
        return { backgroundColor: COLORS.bg.hover, color: COLORS.text.primary };
    }
  };

  const getCompletionRate = () => {
    if (instances.length === 0) return 0;
    const completed = instances.filter(instance => instance.completed).length;
    return Math.round((completed / instances.length) * 100);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setSelectedDate(prev => {
      const newDate = direction === 'prev' ? subDays(prev, 7) : addDays(prev, 7);
      // Refresh progress data for the new date range
      setTimeout(() => {
        updateProgressData(instances, trackingStatus);
      }, 0);
      return newDate;
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <ArrowPathIcon style={{ width: '24px', height: '24px', color: COLORS.text.tertiary, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!habit) {
    return (
      <div style={{ textAlign: 'center', padding: SPACING[32] }}>
        <ExclamationTriangleIcon style={{ margin: '0 auto', height: '48px', width: '48px', color: COLORS.text.tertiary }} />
        <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>Habit not found</p>
      </div>
    );
  }

  const completionRate = getCompletionRate();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: SPACING[12],
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.status.error,
          backgroundColor: '#FEE2E2',
          borderRadius: RADIUS,
          border: `1px solid ${COLORS.status.error}`
        }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8] }} />
          {error}
        </div>
      )}

      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[24], border: `1px solid ${COLORS.border.default}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  padding: SPACING[8],
                  color: COLORS.text.tertiary,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
              >
                <ArrowPathIcon style={{ width: '20px', height: '20px', transform: 'rotate(180deg)' }} />
              </button>
            )}
            <div style={{ padding: SPACING[12], backgroundColor: '#EFF6FF', borderRadius: '50%' }}>
              <ClockIcon style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />
            </div>
            <div>
              <h1 style={{ fontSize: TYPOGRAPHY.headings.sizes.h1, fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.text.primary, fontFamily: TYPOGRAPHY.headings.fontFamily }}>{habit.name}</h1>
              {habit.description && (
                <p style={{ marginTop: SPACING[4], color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.default }}>{habit.description}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: SPACING[8] }}>
            <button
              onClick={handleEdit}
              style={{
                padding: SPACING[8],
                color: COLORS.text.tertiary,
                borderRadius: '50%',
                border: 'none',
                background: 'none',
                cursor: 'pointer'
              }}
              title="Edit habit"
            >
              <PencilIcon style={{ width: '20px', height: '20px' }} />
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: SPACING[8],
                color: COLORS.text.tertiary,
                borderRadius: '50%',
                border: 'none',
                background: 'none',
                cursor: 'pointer'
              }}
              title="Delete habit"
            >
              <TrashIcon style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        </div>

        <div style={{ marginTop: SPACING[24], display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACING[16] }}>
          <div style={{ backgroundColor: COLORS.bg.base, borderRadius: RADIUS, padding: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ChartBarIcon style={{ width: '20px', height: '20px', color: COLORS.text.tertiary }} />
              <span style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>Completion Rate</span>
            </div>
            <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size['4xl'], fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.text.primary }}>{completionRate}%</p>
          </div>
          <div style={{ backgroundColor: '#EFF6FF', borderRadius: RADIUS, padding: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ClockIcon style={{ width: '20px', height: '20px', color: '#60A5FA' }} />
              <span style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: '#3B82F6' }}>Current Streak</span>
            </div>
            <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size['4xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#1E3A8A' }}>{habit.current_streak}</p>
          </div>
          <div style={{ backgroundColor: '#ECFDF5', borderRadius: RADIUS, padding: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ChartBarIcon style={{ width: '20px', height: '20px', color: '#34D399' }} />
              <span style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.status.success }}>Longest Streak</span>
            </div>
            <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size['4xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#064E3B' }}>{habit.longest_streak}</p>
          </div>
          <div style={{ backgroundColor: '#F5F3FF', borderRadius: RADIUS, padding: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon style={{ width: '20px', height: '20px', color: '#A78BFA' }} />
              <span style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: '#7C3AED' }}>Target</span>
            </div>
            <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size['4xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#4C1D95' }}>{habit.target_count}</p>
          </div>
        </div>

        {trackingStatus && (
          <div style={{ marginTop: SPACING[16], display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACING[16] }}>
            <div style={{ backgroundColor: '#ECFDF5', borderRadius: RADIUS, padding: SPACING[12] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.status.success }}>Completed</div>
              <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#064E3B' }}>{trackingStatus.completed_count}</div>
            </div>
            <div style={{ backgroundColor: '#FEE2E2', borderRadius: RADIUS, padding: SPACING[12] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.status.error }}>Not Completed</div>
              <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#7F1D1D' }}>{trackingStatus.not_completed_count}</div>
            </div>
            <div style={{ backgroundColor: '#FEF3C7', borderRadius: RADIUS, padding: SPACING[12] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.status.warning }}>Pending</div>
              <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: TYPOGRAPHY.weight.bold, color: '#78350F' }}>{trackingStatus.pending_count}</div>
            </div>
          </div>
        )}

        <div style={{ marginTop: SPACING[16], display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
          <span style={{
            display: 'inline-block',
            padding: `${SPACING[4]} ${SPACING[12]}`,
            borderRadius: '9999px',
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            ...getFrequencyStyles(habit.frequency)
          }}>
            {habit.frequency.charAt(0).toUpperCase() + habit.frequency.slice(1)}
          </span>
          <span style={{
            display: 'inline-block',
            padding: `${SPACING[4]} ${SPACING[12]}`,
            borderRadius: '9999px',
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            backgroundColor: COLORS.bg.hover,
            color: COLORS.text.primary
          }}>
            {habit.is_active ? 'Active' : 'Inactive'}
          </span>
          {habit.reminder_time && (
            <span style={{
              display: 'inline-block',
              padding: `${SPACING[4]} ${SPACING[12]}`,
              borderRadius: '9999px',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              backgroundColor: '#FEF3C7',
              color: '#92400E'
            }}>
              Reminder: {habit.reminder_time}
            </span>
          )}
        </div>
      </div>

      {/* GitHub-style Progress Graph */}
      <HabitProgressGraph habitId={habitId} habitName={habit.name} />

      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[24], border: `1px solid ${COLORS.border.default}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
          <h2 style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, fontFamily: TYPOGRAPHY.headings.fontFamily }}>Weekly Progress</h2>
          <div style={{ display: 'flex', gap: SPACING[8] }}>
            <button
              onClick={() => navigateWeek('prev')}
              style={{ padding: SPACING[4], color: COLORS.text.tertiary, border: 'none', background: 'none', cursor: 'pointer' }}
            >
              <MinusIcon style={{ width: '16px', height: '16px' }} />
            </button>
            <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>
              {format(startOfWeek(selectedDate), 'MMM d')} - {format(endOfWeek(selectedDate), 'MMM d, yyyy')}
            </span>
            <button
              onClick={() => navigateWeek('next')}
              style={{ padding: SPACING[4], color: COLORS.text.tertiary, border: 'none', background: 'none', cursor: 'pointer' }}
            >
              <PlusIcon style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: SPACING[8] }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{ textAlign: 'center', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, padding: SPACING[8] }}>
              {day}
            </div>
          ))}
          {weeklyProgress.map((day, index) => (
            <button
              key={index}
              onClick={() => toggleHabitCompletion(day.date)}
              disabled={updatingInstance === day.instance?.id}
              style={{
                padding: SPACING[12],
                borderRadius: RADIUS,
                border: '2px solid',
                transition: 'all 0.2s',
                textAlign: 'center',
                cursor: updatingInstance === day.instance?.id ? 'not-allowed' : 'pointer',
                opacity: updatingInstance === day.instance?.id ? 0.5 : 1,
                ...getStatusStyles(day.status)
              }}
            >
              <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium }}>{format(day.date, 'd')}</div>
              {getStatusIcon(day.status)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[24], border: `1px solid ${COLORS.border.default}` }}>
        <h2 style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, marginBottom: SPACING[16], fontFamily: TYPOGRAPHY.headings.fontFamily }}>Monthly Progress</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: SPACING[4] }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{ textAlign: 'center', fontSize: TYPOGRAPHY.size.xs, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, padding: SPACING[4] }}>
              {day}
            </div>
          ))}
          {monthlyProgress.map((day, index) => (
            <button
              key={index}
              onClick={() => toggleHabitCompletion(day.date)}
              disabled={updatingInstance === day.instance?.id}
              style={{
                padding: SPACING[8],
                borderRadius: '4px',
                fontSize: TYPOGRAPHY.size.xs,
                transition: 'all 0.2s',
                textAlign: 'center',
                cursor: updatingInstance === day.instance?.id ? 'not-allowed' : 'pointer',
                opacity: updatingInstance === day.instance?.id ? 0.5 : 1,
                border: '1px solid',
                ...getStatusStyles(day.status)
              }}
              title={format(day.date, 'MMM d, yyyy')}
            >
              {format(day.date, 'd')}
            </button>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[24], border: `1px solid ${COLORS.border.default}` }}>
        <h2 style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, marginBottom: SPACING[16], fontFamily: TYPOGRAPHY.headings.fontFamily }}>Recent Activity</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          {instances
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 10)
            .map(instance => (
              <div key={instance.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: SPACING[12], backgroundColor: COLORS.bg.base, borderRadius: RADIUS }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                  {instance.completed ? (
                    <CheckCircleIcon style={{ width: '20px', height: '20px', color: COLORS.status.success }} />
                  ) : (
                    <XCircleIcon style={{ width: '20px', height: '20px', color: COLORS.status.error }} />
                  )}
                  <span style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                    {format(parseISO(instance.date), 'MMM d, yyyy')}
                  </span>
                  {instance.notes && (
                    <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>- {instance.notes}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: SPACING[8] }}>
                  {instance.notes && (
                    <button
                      onClick={() => setShowNotes(showNotes === instance.id ? null : instance.id)}
                      style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.accent.default, border: 'none', background: 'none', cursor: 'pointer' }}
                    >
                      {showNotes === instance.id ? 'Hide' : 'View'} Notes
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowNotes(showNotes === instance.id ? null : instance.id);
                      setNewNote(instance.notes || '');
                    }}
                    style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    {showNotes === instance.id ? 'Cancel' : 'Edit Notes'}
                  </button>
                </div>
              </div>
            ))}
        </div>

        {instances.length === 0 && (
          <p style={{ textAlign: 'center', color: COLORS.text.secondary, padding: SPACING[16] }}>No activity recorded yet.</p>
        )}
      </div>

      {showNotes !== null && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS, padding: SPACING[24], width: '100%', maxWidth: '448px', margin: SPACING[16] }}>
            <h3 style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, marginBottom: SPACING[16], fontFamily: TYPOGRAPHY.headings.fontFamily }}>Edit Notes</h3>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              style={{
                width: '100%',
                padding: SPACING[12],
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
              rows={4}
              placeholder="Add notes about this habit instance..."
            />
            <div style={{ marginTop: SPACING[16], display: 'flex', justifyContent: 'flex-end', gap: SPACING[8] }}>
              <button
                onClick={() => {
                  setShowNotes(null);
                  setNewNote('');
                }}
                style={{
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  color: COLORS.text.secondary,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const instance = instances.find(inst => inst.id === showNotes);
                  if (instance) {
                    updateInstanceNote(instance.id, newNote);
                  }
                }}
                style={{
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  backgroundColor: COLORS.accent.default,
                  color: '#FFFFFF',
                  borderRadius: RADIUS,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {habit && (
        <HabitForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
          }}
          onHabitCreated={handleHabitUpdated}
          initialHabit={habit}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default HabitDetailView;
